import Cookies from "js-cookie";
import { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import SupportStyleTable from "../../Components/Table/SupportStyleTable";
import {
  fetchComplaintData,
  findSupport,
  findJuniorDevelopers,
  SupportAcceptTask,
  JuniorToSeniorAssignReq,
  SupportHoldTask,
} from "../../Redux/Slice/TaskMasterSlice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../Components/Loader/Loader";
import socket from "../../socket";
import {
  Calendar,
  Clock,
  CheckCircle2,
  Eye,
  UserPlus,
  X,
} from "lucide-react";
import "./InhouseRnd.css";

const formatDateOnly = (isoDate) => {
  if (!isoDate) return "-";
  const date = new Date(isoDate);

  const day = date.getUTCDate().toString().padStart(2, "0");
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const year = date.getUTCFullYear();

  return `${day}/${month}/${year}`;
};

const calculateWorkingSecondsStrict = (startTime, endTime) => {
  let start = new Date(startTime);
  let end = new Date(endTime);
  if (end < start) return 0;

  let totalSeconds = 0;

  let currentDay = new Date(start);
  currentDay.setHours(0, 0, 0, 0);

  let endDay = new Date(end);
  endDay.setHours(0, 0, 0, 0);

  while (currentDay <= endDay) {
    if (currentDay.getDay() !== 0) { // Not Sunday
      let dayStart = new Date(currentDay); dayStart.setHours(10, 0, 0, 0);
      let dayEnd = new Date(currentDay); dayEnd.setHours(19, 0, 0, 0);
      let lunchStart = new Date(currentDay); lunchStart.setHours(13, 30, 0, 0);
      let lunchEnd = new Date(currentDay); lunchEnd.setHours(14, 0, 0, 0);

      let workStart = Math.max(start.getTime(), dayStart.getTime());
      let workEnd = Math.min(end.getTime(), dayEnd.getTime());

      if (workStart < workEnd && workStart < dayEnd.getTime() && workEnd > dayStart.getTime()) {
        let overlap = workEnd - workStart;

        let lStart = Math.max(workStart, lunchStart.getTime());
        let lEnd = Math.min(workEnd, lunchEnd.getTime());

        if (lStart < lEnd) {
          overlap -= (lEnd - lStart);
        }

        totalSeconds += overlap / 1000;
      }
    }
    currentDay.setDate(currentDay.getDate() + 1);
  }
  return Math.floor(totalSeconds);
};

const getFormatForRemainingTime = (timeLimitStr, slabRecords, isHold = false, fallbackDate = null) => {
  if (!timeLimitStr) {
    return { taskWorkTime: "NaN", calcDays: "NaN", estimatedDateStr: "NaN" };
  }

  let allowedSeconds = 0;
  if (timeLimitStr) {
    const parts = timeLimitStr.split(':');
    allowedSeconds = ((parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1], 10) || 0)) * 60;
  }

  let workedSeconds = 0;
  if (slabRecords && Array.isArray(slabRecords) && slabRecords.length > 0) {
    const sorted = [...slabRecords].sort((a, b) => new Date(a.Date) - new Date(b.Date));
    let acceptTime = null;
    if (sorted[0].type === "Hold" && fallbackDate) {
      acceptTime = new Date(fallbackDate);
    }
    for (let i = 0; i < sorted.length; i++) {
      if (sorted[i].type === "Accept") {
        acceptTime = new Date(sorted[i].Date);
      } else if (sorted[i].type === "Hold" && acceptTime) {
        workedSeconds += calculateWorkingSecondsStrict(acceptTime, new Date(sorted[i].Date));
        acceptTime = null;
      }
    }
    if (acceptTime) {
      workedSeconds += calculateWorkingSecondsStrict(acceptTime, new Date());
    }
  } else if (!isHold && fallbackDate) {
    workedSeconds += calculateWorkingSecondsStrict(new Date(fallbackDate), new Date());
  }

  let remainingSec = allowedSeconds - workedSeconds;
  let isNeg = remainingSec < 0;
  remainingSec = Math.abs(remainingSec);

  let days = Math.floor(remainingSec / (510 * 60));
  let leftOverSecsWhole = remainingSec % (510 * 60);

  let leftHours = Math.floor(leftOverSecsWhole / 3600);
  let leftMins = Math.floor((leftOverSecsWhole % 3600) / 60);
  let leftSecs = Math.floor(leftOverSecsWhole % 60);

  let calcDays = `${isNeg && days > 0 ? "-" : ""}${days} Day`;
  if (!isNeg) {
    if (days === 0) calcDays = "Today";
    else if (days === 1) calcDays = "Tommorow";
  }
  let taskWorkTime = `${isNeg ? "- " : ""}${leftHours} Hrs ${leftMins} Mins ${leftSecs} Sec`;

  let estimatedDateStr = "Overdue";
  if (!isNeg) {
    let estDate = new Date();
    let daysToAdd = days;
    while (daysToAdd > 0) {
      estDate.setDate(estDate.getDate() + 1);
      if (estDate.getDay() !== 0) daysToAdd--;
    }
    if (days === 0 && estDate.getDay() === 0) {
      estDate.setDate(estDate.getDate() + 1);
    }
    estimatedDateStr = formatDateOnly(estDate);
  }

  return { taskWorkTime, calcDays, estimatedDateStr, isNeg };
};

const LiveTimeCell = ({ timeLimitStr, slabRecords, isHold, type, fallbackDate }) => {
  const [result, setResult] = useState(getFormatForRemainingTime(timeLimitStr, slabRecords, isHold, fallbackDate));

  useEffect(() => {
    const interval = setInterval(() => {
      setResult(getFormatForRemainingTime(timeLimitStr, slabRecords, isHold, fallbackDate));
    }, 1000);
    return () => clearInterval(interval);
  }, [timeLimitStr, slabRecords, isHold, fallbackDate]);

  if (type === "estimatedDateStr") {
    return (
      <span className="font-semibold text-xs text-slate-700">
        {result.estimatedDateStr === "NaN" ? "--" : result.estimatedDateStr}
      </span>
    );
  }
  if (type === "calcDays") {
    return (
      <span className="font-semibold text-xs text-slate-700">
        {result.calcDays === "NaN" ? "--" : result.calcDays}
      </span>
    );
  }
  if (type === "taskWorkTime") {
    if (!timeLimitStr || result.taskWorkTime === "NaN") {
      return <span className="text-slate-400">--</span>;
    }
    return (
      <span
        className={`font-mono text-xs font-bold px-2 py-0.5 rounded ${result.isNeg
          ? "bg-rose-50 text-rose-700 border border-rose-200"
          : "bg-blue-50 text-blue-700 border border-blue-200"
          }`}
      >
        {result.taskWorkTime}
      </span>
    );
  }

  if (type === "formattedTime") {
    return (
      <span className="font-semibold text-xs text-slate-700">
        {timeLimitStr || "--"}
      </span>
    );
  }

  return <span>--</span>;
};

const DeveloperAvatar = ({ photo, name, size = "w-8 h-8" }) => {
  const [hasError, setHasError] = useState(false);

  const getInitials = (n) => {
    if (!n) return "EM";
    const parts = n.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return n.slice(0, 2).toUpperCase();
  };

  if (photo && !hasError) {
    return (
      <img
        src={photo}
        alt={name || "Developer"}
        className={`${size} rounded-full object-cover shrink-0 border border-slate-200 shadow-sm`}
        onError={() => setHasError(true)}
      />
    );
  }

  return (
    <div
      className={`${size} rounded-full shrink-0 border border-slate-300 bg-gradient-to-br from-blue-100 to-indigo-100 text-[#112A47] flex items-center justify-center text-[11px] font-bold tracking-tight uppercase shadow-sm`}
    >
      {getInitials(name)}
    </div>
  );
};

const SearchableDropdown = ({ options, value, onChange, placeholder }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = (options || []).filter(
    (opt) =>
      (opt.label || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (opt.Role || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = (options || []).find((opt) => String(opt._id) === String(value));

  return (
    <div className="relative mb-3 w-full" ref={dropdownRef}>
      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">{placeholder}</label>
      <div
        className="w-full px-3 py-2 border border-slate-200 rounded-xl cursor-pointer bg-white flex justify-between items-center hover:border-[#112A47] transition-all shadow-sm min-h-[44px]"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          {selectedOption ? (
            <>
              <DeveloperAvatar photo={selectedOption.ProfilePhoto} name={selectedOption.label} size="w-7 h-7" />
              <div className="flex flex-col min-w-0">
                <span className="text-slate-800 font-semibold text-sm truncate">
                  {selectedOption.label}
                </span>
                {selectedOption.Role && (
                  <span className="text-slate-400 font-medium text-xs truncate">
                    {selectedOption.Role}
                  </span>
                )}
              </div>
              {selectedOption.isPresent && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full shrink-0 ml-auto mr-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  Present
                </span>
              )}
            </>
          ) : (
            <span className="text-slate-400 text-sm">Choose support member...</span>
          )}
        </div>
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform duration-200 shrink-0 ${isOpen ? "rotate-180 text-[#112A47]" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-100">
          <div className="p-2 border-b border-slate-100 bg-slate-50/80">
            <input
              type="text"
              className="w-full px-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:border-[#112A47] focus:ring-2 focus:ring-blue-500/20 outline-none transition-all placeholder:text-slate-400"
              placeholder={`Search ${placeholder}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto p-1 divide-y divide-slate-50">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <div
                  key={opt._id}
                  className={`flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer transition-colors rounded-lg my-0.5 ${String(value) === String(opt._id)
                    ? "bg-blue-50 text-[#112A47] font-semibold"
                    : "text-slate-700 hover:bg-slate-100/80 hover:text-slate-900"
                    }`}
                  onClick={() => {
                    onChange(opt._id);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                >
                  <DeveloperAvatar photo={opt.ProfilePhoto} name={opt.label} size="w-8 h-8" />
                  <div className="flex flex-col min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-1.5">
                      <span className="truncate leading-tight font-medium text-slate-800 text-xs">
                        {opt.label}
                      </span>
                      {opt.isPresent && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full shrink-0">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                          Present
                        </span>
                      )}
                    </div>
                    {opt.Role && (
                      <span className="text-[10px] text-slate-400 font-normal leading-tight truncate">
                        {opt.Role}
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-3 text-xs text-slate-400 text-center italic">No members found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Company Initials and Gradient helpers
const getCompanyInitials = (str) => {
  if (!str) return "DS";
  const parts = str.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return str.slice(0, 2).toUpperCase();
};

const getCompanyGradient = (str) => {
  const colors = [
    "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
    "linear-gradient(135deg, #059669 0%, #047857 100%)",
    "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
    "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
    "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
    "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
    "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
  ];
  let hash = 0;
  for (let i = 0; i < (str || "").length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const CustomerReqjunior = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const loginData = Cookies.get("loginData");
  const parsedData = loginData ? JSON.parse(loginData) : null;
  const loggedInUserId = parsedData?.data?._id;

  // Active Tab state persisted in sessionStorage
  const [activeTab, setActiveTab] = useState(() => {
    const s = sessionStorage.getItem("customerReqJuniorActiveTab");
    return s && s !== "all" ? s : "queue";
  });

  useEffect(() => {
    sessionStorage.setItem("customerReqJuniorActiveTab", activeTab);
  }, [activeTab]);

  // Live Current Date & Time Clock
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatLiveDate = (d) => {
    return d.toLocaleDateString("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatLiveTime = (d) => {
    return d.toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }).toUpperCase();
  };

  const { ComplaintData, DataFindSupport, JuniorDevelopers } = useSelector((state) => state.TaskMaster);

  useEffect(() => {
    socket.on("customerReqUpdate", (data) => {
      console.log("Live customer req update received (Junior):", data);
      if (parsedData?.data?._id) {
        dispatch(fetchComplaintData(parsedData.data._id));
      }
    });

    return () => {
      socket.off("customerReqUpdate");
    };
  }, [dispatch, parsedData?.data?._id]);

  const [isSeniorModalOpen, setIsSerniorModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [selectedSeniorId, setSelectedSeniorId] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (parsedData?.data?._id) {
      dispatch(fetchComplaintData(parsedData.data._id));
    }
    dispatch(findSupport());
    dispatch(findJuniorDevelopers());
  }, [dispatch, parsedData?.data?._id]);

  const SeniorOptions = useMemo(() => {
    const map = new Map();
    const seenNames = new Set();

    const addMember = (m, defaultRole) => {
      if (!m || !m._id) return;
      if (loggedInUserId && String(m._id) === String(loggedInUserId)) return;

      const role = (m.Role || defaultRole || "").trim();
      const roleLower = role.toLowerCase();
      if (roleLower.includes("owner")) return;

      const isDelphiSenior =
        roleLower.includes("delphi") &&
        (roleLower.includes("senior") || roleLower.includes("lead"));
      const isSupport = roleLower.includes("support");

      if (!isDelphiSenior && !isSupport) return;

      const name = (m.Name || "").trim();
      if (!name) return;
      const nameKey = name.toLowerCase();
      const idKey = String(m._id);

      if (!map.has(idKey) && !seenNames.has(nameKey)) {
        seenNames.add(nameKey);
        map.set(idKey, {
          label: name,
          _id: m._id,
          ProfilePhoto: m.ProfilePhoto || null,
          Role: role || defaultRole,
          isPresent: Boolean(m.isPresent),
          punchInTime: m.punchInTime || null,
        });
      }
    };

    if (Array.isArray(JuniorDevelopers?.data)) {
      JuniorDevelopers.data.forEach((d) => addMember(d, "Developer"));
    }
    if (Array.isArray(DataFindSupport?.data)) {
      DataFindSupport.data.forEach((s) => addMember(s, "Customer Support"));
    }

    return Array.from(map.values()).sort((a, b) =>
      (a.label || "").localeCompare(b.label || "")
    );
  }, [JuniorDevelopers?.data, DataFindSupport?.data, loggedInUserId]);

  const formatDate = (isoDate) => {
    if (!isoDate) return "-";
    const date = new Date(isoDate);

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? 'PM' : 'AM';

    hours = hours % 12;
    hours = hours ? hours : 12;
    const formattedHours = hours.toString().padStart(2, "0");

    return `${day}/${month}/${year} ${formattedHours}:${minutes} ${ampm}`;
  };

  const handleShowTask = (id) => {
    navigate(`/CustomerReq/CustomerrequustDetail/${id}`);
  };

  const handleRowClick = (row) => {
    const taskId = row?.MainId || row?._id || row?.id;
    if (taskId) {
      handleShowTask(taskId);
    }
  };

  const handleAcceptTask = (tid) => {
    const userId = loggedInUserId;

    if (!userId) {
      toast.error("User not found. Please login again.");
      return;
    }

    dispatch(SupportAcceptTask({ id: userId, tid }))
      .then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          toast.success("Task accepted successfully!");
          dispatch(fetchComplaintData(userId));
        } else {
          toast.error("Failed to accept task. Please try again.");
        }
      })
      .catch((err) => {
        toast.error("An error occurred while accepting task.");
      });
  };

  const handleHoldTask = (tid) => {
    const userId = loggedInUserId;

    if (!userId) {
      toast.error("User not found. Please login again.");
      return;
    }

    dispatch(SupportHoldTask({ id: userId, tid }))
      .then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          toast.success("Task put on hold successfully!");
          dispatch(fetchComplaintData(userId));
        } else {
          toast.error("Failed to hold task.");
        }
      })
      .catch((err) => {
        toast.error("An error occurred while holding task.");
      });
  };

  // --- High-Quality Cell Renderers (Support Dashboard Standards) ---
  const renderTokenCell = ({ value }) => (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full font-bold text-xs shadow-2xs"
      style={{
        backgroundColor: "#EFF6FF",
        color: "#1D4ED8",
        border: "1px solid #BFDBFE",
        letterSpacing: "0.01em",
      }}
    >
      #{value || "--"}
    </span>
  );

  const renderCompanyCell = ({ value }) => {
    const companyName = value || "--";
    const initials = getCompanyInitials(companyName);
    const bgGradient = getCompanyGradient(companyName);

    return (
      <div className="flex items-center gap-2 py-0.5">
        <span
          className="inline-flex justify-center items-center rounded-full font-bold text-white flex-shrink-0 text-[10px]"
          style={{
            width: "28px",
            height: "28px",
            minWidth: "28px",
            minHeight: "28px",
            background: bgGradient,
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12)",
            border: "1.5px solid rgba(255, 255, 255, 0.95)",
            lineHeight: 1,
          }}
        >
          {initials}
        </span>
        <span
          className="font-semibold text-slate-800 text-xs truncate max-w-[210px]"
          title={companyName}
        >
          {companyName}
        </span>
      </div>
    );
  };

  const renderDateCell = ({ value }) => {
    if (!value || value === "-") return <span className="text-slate-400">--</span>;
    const parts = String(value).split(" ");
    const dateStr = parts[0] || "--";
    const timeStr = parts.slice(1).join(" ");

    return (
      <div className="flex flex-col justify-center leading-tight whitespace-nowrap">
        <span className="text-slate-800 font-semibold inline-flex items-center gap-1 text-xs">
          <Calendar size={12} className="text-blue-600" />
          <span>{dateStr}</span>
        </span>
        {timeStr && (
          <span className="text-slate-400 inline-flex items-center gap-1 mt-0.5 text-[11px] font-mono font-medium">
            <Clock size={11} className="text-slate-400" />
            <span>{timeStr}</span>
          </span>
        )}
      </div>
    );
  };

  const renderPersonCell = ({ value }) => {
    if (!value || value === "-" || value === "null") {
      return <span className="text-slate-400 text-xs">--</span>;
    }
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-slate-700 bg-slate-50 border border-slate-200 shadow-2xs whitespace-nowrap"
      >
        <span>👤</span>
        <span className="font-semibold text-slate-800">{value}</span>
      </span>
    );
  };

  // --- Table Column Definitions with Support Dashboard Button Styles ---
  const PendingColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "tokan", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
    {
      field: "acceptTask",
      headerName: "ACCEPT TASK",
      width: 125,
      minWidth: 120,
      maxWidth: 135,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-accept shadow-xs"
          onClick={() => handleAcceptTask(params.row.MainId)}
        >
          <CheckCircle2 size={13} />
          <span>Accept Task</span>
        </button>
      ),
    },
  ];

  const PendingRows = useMemo(() => {
    return (
      ComplaintData?.Pending?.map((el, index) => ({
        id: index + 1,
        tokan: el?.CallNo,
        MainId: el._id,
        CompanyName: el?.CompanyName?.WhaClientName,
        AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
        AssignDate: formatDate(el.Date),
        CompanyId: el.CompanyId?.CompanyName,
        PresentStatus: el?.PresentStatus,
      })) || []
    );
  }, [ComplaintData?.Pending]);

  const HoldingColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "tokan", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    {
      field: "TimeLimit",
      headerName: "TIME LIMIT",
      width: 110,
      minWidth: 105,
      maxWidth: 120,
      renderCell: (params) => <LiveTimeCell type="formattedTime" timeLimitStr={params.row.rawTimeLimit} slabRecords={params.row.rawSlabRecords} isHold={true} fallbackDate={params.row.rawDate} />
    },
    {
      field: "TaskWorkTime",
      headerName: "TASK WORK TIME",
      width: 150,
      minWidth: 140,
      maxWidth: 165,
      renderCell: (params) => <LiveTimeCell type="taskWorkTime" timeLimitStr={params.row.rawTimeLimit} slabRecords={params.row.rawSlabRecords} isHold={true} fallbackDate={params.row.rawDate} />
    },
    {
      field: "Days",
      headerName: "ESTIMATED DAYS",
      width: 125,
      minWidth: 120,
      maxWidth: 135,
      renderCell: (params) => <LiveTimeCell type="calcDays" timeLimitStr={params.row.rawTimeLimit} slabRecords={params.row.rawSlabRecords} isHold={true} fallbackDate={params.row.rawDate} />
    },
    {
      field: "EstimatedDate",
      headerName: "ESTIMATED DATE",
      width: 130,
      minWidth: 125,
      maxWidth: 140,
      renderCell: (params) => <LiveTimeCell type="estimatedDateStr" timeLimitStr={params.row.rawTimeLimit} slabRecords={params.row.rawSlabRecords} isHold={true} fallbackDate={params.row.rawDate} />
    },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
    {
      field: "acceptTask",
      headerName: "ACCEPT TASK",
      width: 125,
      minWidth: 120,
      maxWidth: 135,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-accept shadow-xs"
          onClick={() => handleAcceptTask(params.row.MainId)}
        >
          <CheckCircle2 size={13} />
          <span>Accept Task</span>
        </button>
      ),
    },
  ];

  const HoldingRows = useMemo(() => {
    return (
      ComplaintData?.Hold?.map((el, index) => ({
        id: index + 1,
        tokan: el?.CallNo,
        MainId: el._id,
        CompanyName: el?.CompanyName?.WhaClientName,
        AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
        AssignDate: formatDate(el.Date),
        CompanyId: el.CompanyId?.CompanyName,
        rawTimeLimit: el?.TimeLimit,
        rawSlabRecords: el?.TimeSlabRecord,
        rawDate: el?.Date,
        PresentStatus: el?.PresentStatus,
      })) || []
    );
  }, [ComplaintData?.Hold]);

  const RunningColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "tokan", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    {
      field: "TimeLimit",
      headerName: "TIME LIMIT",
      width: 110,
      minWidth: 105,
      maxWidth: 120,
      renderCell: (params) => <LiveTimeCell type="formattedTime" timeLimitStr={params.row.rawTimeLimit} slabRecords={params.row.rawSlabRecords} isHold={false} fallbackDate={params.row.rawDate} />
    },
    {
      field: "TaskWorkTime",
      headerName: "TASK WORK TIME",
      width: 150,
      minWidth: 140,
      maxWidth: 165,
      renderCell: (params) => <LiveTimeCell type="taskWorkTime" timeLimitStr={params.row.rawTimeLimit} slabRecords={params.row.rawSlabRecords} isHold={false} fallbackDate={params.row.rawDate} />
    },
    {
      field: "Days",
      headerName: "ESTIMATED DAYS",
      width: 125,
      minWidth: 120,
      maxWidth: 135,
      renderCell: (params) => <LiveTimeCell type="calcDays" timeLimitStr={params.row.rawTimeLimit} slabRecords={params.row.rawSlabRecords} isHold={false} fallbackDate={params.row.rawDate} />
    },
    {
      field: "EstimatedDate",
      headerName: "ESTIMATED DATE",
      width: 130,
      minWidth: 125,
      maxWidth: 140,
      renderCell: (params) => <LiveTimeCell type="estimatedDateStr" timeLimitStr={params.row.rawTimeLimit} slabRecords={params.row.rawSlabRecords} isHold={false} fallbackDate={params.row.rawDate} />
    },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
    {
      field: "checkingTask",
      headerName: "CHECKING TASK",
      width: 130,
      minWidth: 125,
      maxWidth: 140,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-check shadow-xs"
          onClick={() => {
            setSelectedTaskId({ MainId: params.row.MainId });
            const defaultSeniorId = params.row.SeniorDeveloperId || "";
            const isValidSupport = SeniorOptions.some(
              (opt) => String(opt._id) === String(defaultSeniorId)
            );
            setSelectedSeniorId(isValidSupport ? defaultSeniorId : "");
            setIsSerniorModalOpen(true);
          }}
        >
          <CheckCircle2 size={13} />
          <span>Check Task</span>
        </button>
      ),
    },
    {
      field: "holdTask",
      headerName: "HOLD TASK",
      width: 110,
      minWidth: 105,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-hold shadow-xs"
          onClick={() => handleHoldTask(params.row.MainId)}
        >
          <Clock size={13} />
          <span>Hold Task</span>
        </button>
      ),
    },
  ];

  const RunningRows = useMemo(() => {
    return (
      ComplaintData?.Accept?.map((el, index) => {
        const seniorId = el?.SeniorDevloperID?._id || (typeof el?.SeniorDevloperID === "string" ? el?.SeniorDevloperID : "");
        return {
          id: index + 1,
          tokan: el?.CallNo,
          MainId: el._id,
          CompanyName: el?.CompanyName?.WhaClientName,
          AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
          SeniorDeveloperId: seniorId,
          SeniorDeveloperObj: el?.SeniorDevloperID,
          AssignDate: formatDate(el.Date),
          CompanyId: el.CompanyId?.CompanyName,
          rawTimeLimit: el?.TimeLimit,
          rawSlabRecords: el?.TimeSlabRecord,
          rawDate: el?.Date,
          PresentStatus: el?.PresentStatus,
        };
      }) || []
    );
  }, [ComplaintData?.Accept]);

  const CompleteColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "tokan", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
  ];

  const CompleteRows = useMemo(() => {
    return (
      ComplaintData?.Complete?.map((el, index) => ({
        id: index + 1,
        MainId: el._id,
        tokan: el?.CallNo,
        CompanyName: el?.CompanyName?.WhaClientName,
        AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
        AssignDate: formatDate(el.Date),
        CompanyId: el.CompanyId?.CompanyName,
        PresentStatus: el?.PresentStatus,
      })) || []
    );
  }, [ComplaintData?.Complete]);

  if (!ComplaintData || ComplaintData === null) {
    return <Loader />;
  }

  return (
    <>
      <div className="w-full dashboard-page-container">
        {/* Support Dashboard Compact Banner with Clock & Badge */}
        <div className="page-header-banner-compact">
          <div>
            <h4 className="mb-0 font-bold">Customer Requirements Dashboard</h4>
            <span className="page-subtitle">
              Track, accept, and resolve customer requirement tasks
            </span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Live Current Date & Time Clock */}
            <div className="dashboard-live-clock" title="Current Live Date & Time">
              <span className="clock-date">
                <Calendar size={14} className="text-blue-600" />
                <span>{formatLiveDate(currentDateTime)}</span>
              </span>
              <span className="clock-divider"></span>
              <span className="clock-time">
                <Clock size={14} className="text-blue-600" />
                <span>{formatLiveTime(currentDateTime)}</span>
                <span className="live-dot" title="Live Clock Running"></span>
              </span>
            </div>

            {/* Total Pending Count Badge */}
            <span
              className="px-2.5 py-1.5 font-bold text-xs rounded-lg shadow-xs flex items-center gap-1.5 bg-amber-400 text-amber-950"
            >
              Pending: {PendingRows.length}
            </span>
          </div>
        </div>

        {/* Status Tabs Bar */}
        <div className="dashboard-tabs-bar">
          <button
            type="button"
            className={`tab-btn-status tab-btn-queue ${activeTab === "queue" ? "active" : ""}`}
            onClick={() => setActiveTab("queue")}
          >
            <span className="tab-status-dot"></span>
            <span>In Queue Task</span>
            <span className="tab-badge">{PendingRows.length}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-holding ${activeTab === "holding" ? "active" : ""}`}
            onClick={() => setActiveTab("holding")}
          >
            <span className="tab-status-dot"></span>
            <span>Holding Task</span>
            <span className="tab-badge">{HoldingRows.length}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-running ${activeTab === "running" ? "active" : ""}`}
            onClick={() => setActiveTab("running")}
          >
            <span className="tab-status-dot"></span>
            <span>Running Task</span>
            <span className="tab-badge">{RunningRows.length}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-complete ${activeTab === "complete" ? "active" : ""}`}
            onClick={() => setActiveTab("complete")}
          >
            <span className="tab-status-dot"></span>
            <span>Complete Task</span>
            <span className="tab-badge">{CompleteRows.length}</span>
          </button>
        </div>

        {/* Main Table Area Filling Remaining Screen Height */}
        <div className="w-full flex-grow flex flex-col min-h-0 overflow-hidden">
          {/* Tab 1: In Queue Task */}
          {activeTab === "queue" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #f59e0b" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-amber-600">
                    In Queue Task
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    New pending customer requests awaiting developer pickup
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {PendingRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="customerreq_jr_queue"
                  fileName="CustomerReq_InQueue_Tasks"
                  columns={PendingColumns}
                  rows={PendingRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No in-queue tasks found."
                />
              </div>
            </div>
          )}

          {/* Tab 2: Holding Task */}
          {activeTab === "holding" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #6366f1" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-indigo-600">
                    Holding Task
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    Customer tasks placed on temporary hold
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {HoldingRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="customerreq_jr_holding"
                  fileName="CustomerReq_Holding_Tasks"
                  columns={HoldingColumns}
                  rows={HoldingRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No holding tasks found."
                />
              </div>
            </div>
          )}

          {/* Tab 3: Running Task */}
          {activeTab === "running" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #1d4ed8" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-blue-600">
                    Running Task
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    Currently active customer requirement tasks
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {RunningRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="customerreq_jr_running"
                  fileName="CustomerReq_Running_Tasks"
                  columns={RunningColumns}
                  rows={RunningRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No running tasks found."
                />
              </div>
            </div>
          )}

          {/* Tab 4: Complete Task */}
          {activeTab === "complete" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #15803d" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-emerald-600">
                    Complete Task
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    Successfully finished and closed customer requirement tasks
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {CompleteRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="customerreq_jr_complete"
                  fileName="CustomerReq_Complete_Tasks"
                  columns={CompleteColumns}
                  rows={CompleteRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No completed tasks found."
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal for Senior Developer */}
      {isSeniorModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-[90%] max-w-md p-6 border border-slate-100">
            <h2 className="text-lg font-bold mb-4 text-[#112A47]">
              Select Support Member
            </h2>

            <SearchableDropdown
              options={SeniorOptions}
              value={selectedSeniorId}
              onChange={(id) => setSelectedSeniorId(id)}
              placeholder="Support Member"
            />

            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter instructions / notes..."
              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl mb-4 resize-none focus:outline-none focus:ring-2 focus:ring-[#112A47]/20 focus:border-[#112A47]"
              rows={3}
            ></textarea>

            <div className="flex justify-end gap-2.5 pt-2 border-t border-slate-100">
              <button
                type="button"
                className="px-4 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition"
                onClick={() => {
                  setIsSerniorModalOpen(false);
                  setSelectedSeniorId("");
                  setSelectedTaskId(null);
                  setDescription("");
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!selectedSeniorId}
                className="px-5 py-1.5 text-xs font-semibold bg-[#112A47] hover:bg-[#0c2545] text-white rounded-lg shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => {
                  const juniorId = loggedInUserId;

                  dispatch(
                    JuniorToSeniorAssignReq({
                      SuportTeamId: selectedSeniorId,
                      SupportTeamId: selectedSeniorId,
                      complainBoxId: selectedTaskId?.MainId,
                      DescripTione: description,
                      JuniorDevloperID: juniorId,
                    })
                  ).then((res) => {
                    if (res.meta.requestStatus === "fulfilled") {
                      toast.success("Task assigned for checking successfully!");
                      if (juniorId) {
                        dispatch(fetchComplaintData(juniorId));
                      }
                    } else {
                      toast.error("Failed to assign task. Please try again.");
                    }
                  });

                  setIsSerniorModalOpen(false);
                  setSelectedSeniorId("");
                  setSelectedTaskId(null);
                  setDescription("");
                }}
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerReqjunior;
